// Collection product cards on search page.
// Uses event delegation so behavior survives AJAX re-renders from facets/sorting.
(function () {
  if (window.__collectionProductCardsBound) return;
  window.__collectionProductCardsBound = true;

  const CONDITION_PRIORITY = [
    'near mint',
    'lightly played',
    'moderately played',
    'heavily played',
    'damaged'
  ];

  function getVariantPriority(option) {
    const title = (option.dataset.title || option.textContent || '').toLowerCase();
    const inventory = parseInt(option.dataset.inventory || '0', 10);
    const conditionIndex = CONDITION_PRIORITY.findIndex((condition) => title.includes(condition));

    return {
      stockRank: inventory > 0 ? 0 : 1,
      conditionRank: conditionIndex >= 0 ? conditionIndex : CONDITION_PRIORITY.length,
      foilRank: title.includes('foil') ? 1 : 0
    };
  }

  function isHigherPriorityVariant(candidate, current) {
    if (!current) return true;

    if (candidate.stockRank !== current.stockRank) {
      return candidate.stockRank < current.stockRank;
    }

    if (candidate.conditionRank !== current.conditionRank) {
      return candidate.conditionRank < current.conditionRank;
    }

    return candidate.foilRank < current.foilRank;
  }

  function applyPreferredDefaultSelection(select) {
    if (!select || select.dataset.userSelected === 'true') return;

    const options = Array.from(select.options || []);
    if (options.length <= 1) return;

    let bestIndex = -1;
    let bestPriority = null;

    options.forEach((option, index) => {
      const priority = getVariantPriority(option);
      if (isHigherPriorityVariant(priority, bestPriority)) {
        bestPriority = priority;
        bestIndex = index;
      }
    });

    if (bestIndex >= 0 && select.selectedIndex !== bestIndex) {
      select.selectedIndex = bestIndex;
    }
  }

  function syncCardFromSelect(select) {
    if (!select) return;
    applyPreferredDefaultSelection(select);

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;

    const variantId = selectedOption.value;
    const price = selectedOption.dataset.price;
    const money = selectedOption.dataset.money;
    const image = selectedOption.dataset.image;
    const inventory = parseInt(selectedOption.dataset.inventory || '0', 10);
    const isAvailable = selectedOption.dataset.available === 'true';
    const card = select.closest('.collection-product-card');
    if (!card) return;

    const priceElement = card.querySelector('.collection-product-price');
    if (priceElement && price) {
      if (money) {
        priceElement.textContent = money;
      } else {
        const activeCurrency = window?.Shopify?.currency?.active || 'USD';
        priceElement.textContent = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: activeCurrency,
          minimumFractionDigits: 2
        }).format(price / 100);
      }
      priceElement.dataset.price = price;
    }

    const productImage = card.querySelector('.collection-product-image');
    if (productImage && image) {
      productImage.src = image;
    }

    const stockElement = card.querySelector('[data-inventory-count]');
    if (stockElement) {
      if (isAvailable && inventory > 0) {
        stockElement.textContent = `${inventory} in stock`;
        stockElement.style.display = '';
      } else {
        stockElement.textContent = '';
        stockElement.style.display = 'none';
      }
    }

    const addToCartBtn = card.querySelector('.collection-add-to-cart-btn');
    if (addToCartBtn) {
      const defaultText = addToCartBtn.dataset.defaultText || 'Add to Cart';
      const canAddToCart = isAvailable && inventory > 0;
      addToCartBtn.dataset.variantId = variantId;
      addToCartBtn.disabled = !canAddToCart;
      addToCartBtn.textContent = canAddToCart ? defaultText : 'Sold Out';
    }
  }

  function syncAllVisibleCards() {
    document.querySelectorAll('.collection-variant-select').forEach(syncCardFromSelect);
  }

  async function handleAddToCart(button) {
    if (!button || button.disabled) return;
    const variantId = button.dataset.variantId;
    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    button.classList.add('loading');
    button.disabled = true;

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const canRenderDrawerFromResponse =
        cartDrawer &&
        typeof cartDrawer.getSectionsToRender === 'function' &&
        typeof cartDrawer.renderContents === 'function';
      let data;

      if (canRenderDrawerFromResponse && typeof routes !== 'undefined' && routes.cart_add_url) {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append(
          'sections',
          cartDrawer.getSectionsToRender().map((section) => section.id).join(',')
        );
        formData.append('sections_url', window.location.pathname);

        const response = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: formData
        });
        data = await response.json();
      } else {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });
        data = await response.json();
      }

      if (data.status && data.status === 422) {
        alert(data.description || 'Unable to add item to cart');
      } else {
        button.textContent = 'Added!';
        const defaultText = button.dataset.defaultText || 'Add to Cart';
        setTimeout(() => {
          button.textContent = defaultText;
        }, 2000);

        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'collection-product-card', productVariantId: variantId });
        }

        if (typeof window.updateStickyCart === 'function') {
          window.updateStickyCart();
        }

        if (canRenderDrawerFromResponse && data.sections) {
          cartDrawer.renderContents(data);
        } else if (cartDrawer) {
          setTimeout(() => {
            cartDrawer.open();
          }, 100);
        }
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding item to cart. Please try again.');
    } finally {
      button.classList.remove('loading');
      const card = button.closest('.collection-product-card');
      const select = card ? card.querySelector('.collection-variant-select') : null;
      if (select) {
        syncCardFromSelect(select);
      } else if (!button.textContent.includes('Sold Out')) {
        button.disabled = false;
      }
    }
  }

  document.addEventListener('change', (event) => {
    const select = event.target.closest('.collection-variant-select');
    if (!select) return;
    select.dataset.userSelected = 'true';
    syncCardFromSelect(select);
  });

  document.addEventListener('click', (event) => {
    const variantSelector = event.target.closest('.collection-variant-selector');
    if (variantSelector) {
      event.stopPropagation();
    }

    const button = event.target.closest('.collection-add-to-cart-btn');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    handleAddToCart(button);
  });

  document.addEventListener('DOMContentLoaded', () => {
    syncAllVisibleCards();
  });

  // Facets re-render #ProductGridContainer via AJAX; keep cards in sync after each grid swap.
  const gridContainer = document.getElementById('ProductGridContainer');
  if (gridContainer && 'MutationObserver' in window) {
    let syncQueued = false;
    const observer = new MutationObserver(() => {
      if (syncQueued) return;
      syncQueued = true;
      requestAnimationFrame(() => {
        syncAllVisibleCards();
        syncQueued = false;
      });
    });
    // Observe only direct children to avoid feedback loops from text updates within cards.
    observer.observe(gridContainer, { childList: true });
  }
})();
